import React, { useEffect, useCallback, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { Card, Typography, Spin, Button, Space, Tooltip } from "antd";
import { CrownOutlined } from "@ant-design/icons";
import { types, errorAction } from "@authzsvc/api-lib";
import {
  showErrorNotification,
  showInfoNotification,
  showSuccessNotification,
  showWarningNotification,
} from "../../util";
import { ContentTable, SearchBar } from "@authzsvc/common-react-components";
import {
  defaultGroupTableColumns,
  DEFAULT_GROUP_TABLE_QUERY_FIELDS,
} from "../../constants";
import { isApiCallLoading } from "../../selectors";
import {
  getGroupsIOwn,
  getGroupsModeratedOrRead,
  getIdentityById,
  getVisibleGroupsForIdentity,
  addGroupMemberIdentities,
  addGroupMemberGroups,
  deleteGroupMemberIdentities,
  deleteGroupMemberGroups,
} from "../../actions/api";
import AddMemberSearchModal from "../common/AddMemberSearchModal";
import IdentitySearchSelect from "../common/IdentitySearchSelect";
import BulkErrorsModal from "../common/BulkErrorsModal";

const GroupAdmin = () => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const ownGroups = useSelector((state) => state.groups.groupsIOwn);
  const loadingOwnedGroups = useSelector((state) =>
    isApiCallLoading(state, types.GET_GROUPS_I_OWN)
  );
  const loadingModeratedGroups = useSelector((state) =>
    isApiCallLoading(state, types.GET_GROUPS_MODERATED_OR_READ)
  );
  const loading = loadingOwnedGroups || loadingModeratedGroups;
  const [searchValue, setSearchValue] = useState(
    searchParams.get("search") || ""
  );
  const [currentPage, setCurrentPage] = useState(() =>
    parseInt(sessionStorage.getItem("groupAdmin_currentPage") || "1", 10)
  );
  const isFirstFetch = useRef(true);
  const [currentFilter, setCurrentFilter] = useState(
    searchParams.get("filter") || "Contains"
  );
  const [pageSize, setPageSize] = useState(10);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [identityFilterMode, setIdentityFilterMode] = useState(false);
  const [filteredGroups, setFilteredGroups] = useState([]);
  const [visibleGroupIds, setVisibleGroupIds] = useState([]);
  const [selectedIdentity, setSelectedIdentity] = useState(null);
  const [isInitializedFromUrl, setIsInitializedFromUrl] = useState(false);
  const [bulkModalMode, setBulkModalMode] = useState(null); // modal mode: null | 'add' | 'remove'
  const [loadingBulkMemberSubscription, setLoadingBulkMemberSubscription] =
    useState(false);
  const [loadingBulkMemberRemoval, setLoadingBulkMemberRemoval] =
    useState(false);
  const [bulkErrorsVisible, setBulkErrorsVisible] = useState(false);
  const [bulkErrors, setBulkErrors] = useState([]);

  /**
   * Fetches all groups with pagination support from a single endpoint
   * Includes retry logic for transient network errors
   * @param {Function} actionCreator - The action creator function (e.g., getGroupsIOwn)
   * @param {Object} queryFilter - The query filter object
   * @param {string} actionType - The action type constant for error checking
   * @param {number} maxRetries - Maximum number of retry attempts per page (default: 3)
   * @returns {Promise<Object>} - { error: boolean, data?: Array, response?: Object }
   */
  const fetchAllPagesFromEndpoint = useCallback(
    async (actionCreator, queryFilter, actionType, maxRetries = 3) => {
      let allGroups = [];
      let token = null;
      let hasMore = true;

      while (hasMore) {
        const currentFilter = token ? { ...queryFilter, token } : queryFilter;
        let response = null;
        let lastError = null;

        // Retry logic for each page
        for (let attempt = 0; attempt <= maxRetries; attempt++) {
          response = await dispatch(actionCreator(currentFilter));

          if (
            response.type === errorAction(actionType) ||
            !response.payload?.data
          ) {
            lastError = response;
            // If this is not the last attempt, wait before retrying
            if (attempt < maxRetries) {
              await new Promise((resolve) =>
                setTimeout(resolve, 1000 * (attempt + 1))
              );
              continue;
            }
            // All retries failed
            return { error: true, response: lastError };
          }

          // Success - break out of retry loop
          break;
        }

        const groups = response.payload.data;
        allGroups = allGroups.concat(groups);

        // Check if there's a next page
        const pagination = response.payload.pagination;
        if (pagination && pagination.token) {
          token = pagination.token;
        } else {
          hasMore = false;
        }
      }

      return { error: false, data: allGroups };
    },
    [dispatch]
  );

  /**
   * Fetches and merges groups from both "owned/admin" and "moderated/read" endpoints
   * Handles pagination for both endpoints and deduplicates by group ID
   * Dispatches a manual success action to update Redux store
   * @param {Object} baseQueryFilter - Optional query filter (for search/filter/limit)
   * @returns {Promise<void>}
   */
  const fetchAllOwnedAndModeratedGroups = useCallback(
    async (baseQueryFilter = {}) => {
      const queryFilter = {
        field: DEFAULT_GROUP_TABLE_QUERY_FIELDS,
        ...baseQueryFilter,
      };

      // Fetch all pages from the first endpoint (owned/admin)
      const ownedResult = await fetchAllPagesFromEndpoint(
        getGroupsIOwn,
        queryFilter,
        types.GET_GROUPS_I_OWN
      );
      if (ownedResult.error) {
        return ownedResult.response;
      }

      // Fetch all pages from the second endpoint (moderator/reader)
      const moderatorReadFilter = {
        field: DEFAULT_GROUP_TABLE_QUERY_FIELDS,
        includeReaderOf: false,
        ...baseQueryFilter,
      };
      const moderatedReadResult = await fetchAllPagesFromEndpoint(
        getGroupsModeratedOrRead,
        moderatorReadFilter,
        types.GET_GROUPS_MODERATED_OR_READ
      );
      if (moderatedReadResult.error) {
        return moderatedReadResult.response;
      }

      // Merge and deduplicate by group ID
      const groupMap = new Map();

      ownedResult.data.forEach((group) => {
        if (group.id) {
          groupMap.set(group.id, group);
        }
      });

      moderatedReadResult.data.forEach((group) => {
        if (group.id && !groupMap.has(group.id)) {
          groupMap.set(group.id, group);
        }
      });

      const mergedGroups = Array.from(groupMap.values());

      // Manually dispatch a success action to update the Redux store
      // This mimics what the API action creator would do
      dispatch({
        type: `${types.GET_GROUPS_I_OWN}_SUCCESS`,
        payload: {
          data: mergedGroups,
        },
      });
    },
    [dispatch, fetchAllPagesFromEndpoint]
  );

  const columnsWithSorting = defaultGroupTableColumns.map((column) => ({
    ...column,
    sorter: (a, b) => {
      const aValue = a[column.dataIndex];
      const bValue = b[column.dataIndex];

      if (typeof aValue === "string" && typeof bValue === "string") {
        return aValue.localeCompare(bValue);
      }

      if (aValue && bValue && aValue.displayName && bValue.displayName) {
        return aValue.displayName.localeCompare(bValue.displayName);
      }

      if (aValue < bValue) return -1;
      if (aValue > bValue) return 1;
      return 0;
    },
  }));

  // Only allow selection of non-replica, non-dynamic groups
  const isSelectableGroup = (group) =>
    group.syncType !== "Slave" && group.dynamic !== true;
  const validGroups = (identityFilterMode ? filteredGroups : ownGroups).filter(
    isSelectableGroup
  );

  const getDisabledReason = (group) => {
    if (group.syncType === "Slave")
      return "You cannot manage members of replica groups in the Groups Portal.";
    if (group.dynamic === true)
      return "You cannot manage members of dynamic groups.";
    return "";
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: (keys, rows) => {
      // Only allow valid group keys
      const filteredKeys = keys.filter((key) => {
        const group = (identityFilterMode ? filteredGroups : ownGroups).find(
          (g) => g.id === key
        );
        return group && isSelectableGroup(group);
      });
      setSelectedRowKeys(filteredKeys);
    },
    getCheckboxProps: (record) => {
      const disabled = !isSelectableGroup(record);
      return {
        disabled,
      };
    },
    renderCell: (checked, record, index, originNode) => {
      const disabled = !isSelectableGroup(record);
      const reason = getDisabledReason(record);
      if (disabled && reason) {
        return (
          <Tooltip title={reason} placement="right">
            <span>{originNode}</span>
          </Tooltip>
        );
      }
      return originNode;
    },
    onSelectAll: (selected, selectedRows, changeRows) => {
      const keys = selected ? validGroups.map((group) => group.id) : [];
      setSelectedRowKeys(keys);
    },
  };

  const hasSelected = selectedRowKeys.length > 0;

  const onReset = () => {
    setSelectedRowKeys([]);
  };

  const handleOpenBulkMemberRemovalModal = async () => {
    setBulkModalMode("remove");
  };

  const handleOpenBulkMemberSubscriptionModal = async () => {
    setBulkModalMode("add");
  };

  const handleBulkMemberSubscription = async (
    record,
    comment = "",
    emailUsedInSearch = null
  ) => {
    setLoadingBulkMemberSubscription(true);
    const selectedGroups = getSelectedGroupsData();
    let successCount = 0;
    let failureCount = 0;
    let alreadyMemberCount = 0;
    const collectedErrors = [];

    const groupsToAdd = selectedGroups;

    if (record.groupIdentifier) {
      for (const group of groupsToAdd) {
        const response = await dispatch(
          addGroupMemberGroups(group.id, [record.id])
        );
        if (response.type === errorAction(types.ADD_GROUP_MEMBER_GROUPS)) {
          collectedErrors.push({
            groupId: group.id,
            displayName: getGroupIdentifierById(group.id),
            message: response.payload?.data?.message,
            httpRequestId: response.payload?.data?.httpRequestId,
          });
          failureCount++;
        } else {
          // Check if data is empty (already member) or has content (actually added)
          if (response.payload?.data && response.payload.data.length === 0) {
            alreadyMemberCount++;
          } else {
            successCount++;
          }
        }
      }

      // Enhanced notification for groups
      const memberName = record.groupIdentifier;
      showBulkOperationNotification(
        "Group",
        memberName,
        successCount,
        alreadyMemberCount,
        failureCount
      );
    } else {
      for (const group of groupsToAdd) {
        const emailToUpdate = emailUsedInSearch?.endsWith("@cern.ch")
          ? null
          : emailUsedInSearch;
        const response = await dispatch(
          addGroupMemberIdentities(group.id, [
            {
              id: record.id,
              comment: comment || "",
              mailingAddress: emailToUpdate,
            },
          ])
        );
        if (response.type === errorAction(types.ADD_GROUP_MEMBER_IDENTITIES)) {
          collectedErrors.push({
            groupId: group.id,
            displayName: getGroupIdentifierById(group.id),
            message: response.payload?.data?.message,
            httpRequestId: response.payload?.data?.httpRequestId,
          });
          failureCount++;
        } else {
          // Check if data is empty (already member) or has content (actually added)
          if (response.payload?.data && response.payload.data.length === 0) {
            alreadyMemberCount++;
          } else {
            successCount++;
          }
        }
      }

      // Enhanced notification for identities
      const memberName = record.displayName;
      showBulkOperationNotification(
        "Identity",
        memberName,
        successCount,
        alreadyMemberCount,
        failureCount
      );
    }

    if (collectedErrors.length > 0) {
      openBulkErrorsModal(collectedErrors);
    } else {
      setBulkModalMode(null);
      setSelectedRowKeys([]);
    }
    setLoadingBulkMemberSubscription(false);
  };

  const handleBulkMemberSubscriptionByEmail = async (email) => {
    setLoadingBulkMemberSubscription(true);
    const groupsToProcess = getSelectedGroupsData();
    let successCount = 0;
    let failureCount = 0;
    let alreadyMemberCount = 0;
    const collectedErrors = [];

    const emailToUpdate = email?.endsWith("@cern.ch") ? null : email;
    const identityData = [
      { id: email, comment: "", mailingAddress: emailToUpdate },
    ];

    for (const group of groupsToProcess) {
      const response = await dispatch(
        addGroupMemberIdentities(group.id, identityData)
      );

      if (response.type === errorAction(types.ADD_GROUP_MEMBER_IDENTITIES)) {
        collectedErrors.push({
          groupId: group.id,
          displayName: getGroupIdentifierById(group.id),
          message: response.payload?.data?.message,
          httpRequestId: response.payload?.data?.httpRequestId,
        });
        failureCount++;
      } else {
        // Check if data is empty (already member) or has content (actually added)
        if (response.payload?.data && response.payload.data.length === 0) {
          alreadyMemberCount++;
        } else {
          successCount++;
        }
      }
    }

    // Enhanced notification for email additions
    showBulkOperationNotification(
      "Email",
      email,
      successCount,
      alreadyMemberCount,
      failureCount
    );

    if (collectedErrors.length > 0) {
      openBulkErrorsModal(collectedErrors);
    } else {
      setBulkModalMode(null);
      setSelectedRowKeys([]);
    }
    setLoadingBulkMemberSubscription(false);
  };

  const handleBulkMemberRemoval = async (record) => {
    setLoadingBulkMemberRemoval(true);
    const selectedGroups = getSelectedGroupsData();
    let successCount = 0;
    let failureCount = 0;
    let notMemberCount = 0;
    const collectedErrors = [];

    if (record.groupIdentifier) {
      for (const group of selectedGroups) {
        const response = await dispatch(
          deleteGroupMemberGroups(group.id, [record.id])
        );
        if (response.type === errorAction(types.DELETE_GROUP_MEMBER_GROUPS)) {
          collectedErrors.push({
            groupId: group.id,
            displayName: getGroupIdentifierById(group.id),
            message: response.payload?.data?.message,
            httpRequestId: response.payload?.data?.httpRequestId,
          });
          failureCount++;
        } else {
          // Check if data is empty (was not a member) or has content (actually removed)
          if (response.payload?.data && response.payload.data.length === 0) {
            notMemberCount++;
          } else {
            successCount++;
          }
        }
      }

      // Enhanced notification for groups
      const memberName = record.groupIdentifier;
      showBulkOperationNotification(
        "Group",
        memberName,
        successCount,
        notMemberCount,
        failureCount,
        true
      );
    } else {
      for (const group of selectedGroups) {
        const response = await dispatch(
          deleteGroupMemberIdentities(group.id, [record.id])
        );
        if (
          response.type === errorAction(types.DELETE_GROUP_MEMBER_IDENTITIES)
        ) {
          collectedErrors.push({
            groupId: group.id,
            displayName: getGroupIdentifierById(group.id),
            message: response.payload?.data?.message,
            httpRequestId: response.payload?.data?.httpRequestId,
          });
          failureCount++;
        } else {
          // Check if data is empty (was not a member) or has content (actually removed)
          if (response.payload?.data && response.payload.data.length === 0) {
            notMemberCount++;
          } else {
            successCount++;
          }
        }
      }

      // Enhanced notification for identities
      const memberName = record.displayName;
      showBulkOperationNotification(
        "Identity",
        memberName,
        successCount,
        notMemberCount,
        failureCount,
        true
      );
    }

    if (collectedErrors.length > 0) {
      openBulkErrorsModal(collectedErrors);
    } else {
      setBulkModalMode(null);
      setSelectedRowKeys([]);
    }
    setLoadingBulkMemberRemoval(false);
  };

  const getSelectedGroupsData = () => {
    const currentGroups = identityFilterMode ? filteredGroups : ownGroups;
    // Only return selected and valid groups
    const selected = currentGroups.filter(
      (group) => selectedRowKeys.includes(group.id) && isSelectableGroup(group)
    );
    return selected;
  };

  const getGroupIdentifierById = (id) => {
    const groups = identityFilterMode ? filteredGroups : ownGroups;
    const matchedGroup = (groups || []).find((g) => g.id === id);
    return matchedGroup ? matchedGroup.groupIdentifier : null;
  };

  const openBulkErrorsModal = (errors = []) => {
    if (!errors || errors.length === 0) return;
    setBulkErrors(errors);
    setBulkErrorsVisible(true);
  };

  const showBulkOperationNotification = (
    memberType,
    memberName,
    successCount,
    specialCount, // alreadyMemberCount for addition, notMemberCount for removal
    failureCount,
    isRemoval = false
  ) => {
    if (successCount === 0 && specialCount === 0 && failureCount === 0) return;

    const pluralize = (count) => (count > 1 ? "s" : "");
    const operation = isRemoval ? "Removal" : "Addition";
    const operationVerb = isRemoval ? "removed from" : "added to";
    const operationVerbPast = isRemoval ? "removed from" : "added to";
    const specialCondition = isRemoval
      ? "was not member of"
      : "already member of";

    if (successCount > 0 && specialCount > 0) {
      showWarningNotification(
        `Bulk ${memberType} ${operation} Partially Completed`,
        `${memberType} "${memberName}" ${operationVerb} ${successCount} group${pluralize(
          successCount
        )}, ${specialCondition} ${specialCount} group${pluralize(
          specialCount
        )}${
          failureCount > 0
            ? `, and could not be ${operationVerb} ${failureCount} group${pluralize(
                failureCount
              )}`
            : ""
        }.`
      );
    } else if (successCount > 0 && failureCount > 0) {
      showWarningNotification(
        `Bulk ${memberType} ${operation} Partially Completed`,
        `${memberType} "${memberName}" successfully ${operationVerbPast} ${successCount} group${pluralize(
          successCount
        )}, but could not be ${operationVerb} ${failureCount} group${pluralize(
          failureCount
        )}.`
      );
    } else if (successCount > 0) {
      showSuccessNotification(
        `Bulk ${memberType} ${operation} Completed`,
        `${memberType} "${memberName}" successfully ${operationVerbPast} ${successCount} group${pluralize(
          successCount
        )}${
          failureCount > 0
            ? `, but could not be ${operationVerb} ${failureCount} group${pluralize(
                failureCount
              )}`
            : ""
        }.`
      );
    } else if (specialCount > 0 && failureCount > 0) {
      showWarningNotification(
        `Bulk ${memberType} ${operation} Partially Completed`,
        `${memberType} "${memberName}" ${specialCondition} ${specialCount} group${pluralize(
          specialCount
        )}, and could not be ${operationVerb} ${failureCount} group${pluralize(
          failureCount
        )}.`
      );
    } else if (specialCount > 0) {
      const specialMessage = isRemoval
        ? `${memberType} "${memberName}" was not a member of any of the ${specialCount} selected group${pluralize(
            specialCount
          )}.`
        : `${memberType} "${memberName}" is already a member of all ${specialCount} selected group${pluralize(
            specialCount
          )}.`;

      showInfoNotification("No changes made", specialMessage);
    } else if (failureCount > 0) {
      showErrorNotification(
        `Bulk ${memberType} ${operation} Failed`,
        `${memberType} "${memberName}" could not be ${operationVerb} ${failureCount} group${pluralize(
          failureCount
        )}.`
      );
    }
  };

  const handleIdentitySelect = useCallback(
    async (identity) => {
      setSelectedIdentity(identity);
      setIdentityFilterMode(true);

      // Fetch all visible groups for the identity with pagination
      const queryFilter = {
        field: DEFAULT_GROUP_TABLE_QUERY_FIELDS,
      };

      // Create a wrapper function that includes the identity ID
      const fetchVisibleGroups = (filter) =>
        getVisibleGroupsForIdentity(identity.id, filter);

      const result = await fetchAllPagesFromEndpoint(
        fetchVisibleGroups,
        queryFilter,
        types.GET_VISIBLE_GROUPS_FOR_IDENTITY
      );

      if (result.error) {
        showErrorNotification(
          "Failed to load visible groups",
          result.response?.payload?.data?.message ||
            "An error occurred while loading groups for this identity"
        );
        return;
      }

      const ids = result.data.map((group) => group.id);
      setVisibleGroupIds(ids);
      const filtered = ownGroups.filter((group) => ids.includes(group.id));
      setFilteredGroups(filtered);
    },
    [fetchAllPagesFromEndpoint, ownGroups]
  );

  const clearIdentityFilter = () => {
    setIdentityFilterMode(false);
    setSelectedIdentity(null);
    setFilteredGroups([]);
    setVisibleGroupIds([]);
    setSelectedRowKeys([]);
  };

  useEffect(() => {
    if (identityFilterMode && visibleGroupIds && visibleGroupIds.length > 0) {
      const filtered = ownGroups.filter((group) =>
        visibleGroupIds.includes(group.id)
      );
      setFilteredGroups(filtered);
      setSelectedRowKeys((prevKeys) =>
        prevKeys.filter((k) => filtered.some((g) => g.id === k))
      );
    }
  }, [ownGroups, identityFilterMode, visibleGroupIds]);

  useEffect(() => {
    sessionStorage.setItem("groupAdmin_currentPage", currentPage);
  }, [currentPage]);

  const handleSearch = useCallback(
    async (text = searchValue, filter = currentFilter, limit) => {
      setCurrentFilter(filter || "Contains");

      if (isFirstFetch.current) {
        isFirstFetch.current = false;
      } else {
        setCurrentPage(1);
      }

      const queryFilter = {
        field: DEFAULT_GROUP_TABLE_QUERY_FIELDS,
      };

      if (text && filter) {
        queryFilter.filter = `groupIdentifier:${filter}:${text.trim()}`;
      } else if (text) {
        queryFilter.filter = `groupIdentifier:${text.trim()}`;
      }

      if (limit) {
        queryFilter.limit = limit;
      }

      // Always use both endpoints with pagination and merge to include moderated groups
      const response = await fetchAllOwnedAndModeratedGroups(queryFilter);
      if (response?.type && response.type.includes("ERROR")) {
        showErrorNotification(
          response.payload.title,
          response.payload.data.message
        );
      }
    },
    [currentFilter, fetchAllOwnedAndModeratedGroups, searchValue]
  );

  useEffect(() => {
    const identityId = searchParams.get("identityId");

    if (!identityId) {
      setIsInitializedFromUrl(true);
      return;
    }

    const initializeIdentityFilter = async () => {
      try {
        const result = await dispatch(getIdentityById(identityId, {}));
        if (result?.payload?.data) {
          await handleIdentitySelect(result.payload.data);
        }
      } catch (error) {
        console.error("Failed to load identity:", error);
      } finally {
        setIsInitializedFromUrl(true);
      }
    };

    initializeIdentityFilter();
  }, [dispatch, handleIdentitySelect, searchParams]);

  useEffect(() => {
    if (!isInitializedFromUrl) return;

    handleSearch(searchValue, currentFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInitializedFromUrl]);

  useEffect(() => {
    if (!isInitializedFromUrl) return;

    const timer = setTimeout(() => {
      const newParams = new URLSearchParams();
      if (searchValue) newParams.set("search", searchValue);
      newParams.set("filter", currentFilter);
      if (selectedIdentity?.id)
        newParams.set("identityId", selectedIdentity.id);

      if (searchParams.toString() !== newParams.toString()) {
        setSearchParams(newParams, { replace: true });
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [
    currentFilter,
    isInitializedFromUrl,
    searchParams,
    searchValue,
    selectedIdentity,
    setSearchParams,
  ]);

  return (
    <Card
      title={
        <Typography.Title level={4} style={{ margin: 0 }}>
          <CrownOutlined style={{ marginRight: 8, color: "#bfbfbf" }} />
          Groups I own or manage
        </Typography.Title>
      }
      extra={
        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          <Space>
            {hasSelected && (
              <>
                <Typography.Text type="secondary">
                  Selected {selectedRowKeys.length} items
                </Typography.Text>
                <Button
                  id="resetSelectionButton"
                  type="primary"
                  ghost
                  onClick={onReset}
                  disabled={
                    !hasSelected ||
                    loadingBulkMemberSubscription ||
                    loadingBulkMemberRemoval
                  }
                  loading={
                    loadingBulkMemberSubscription || loadingBulkMemberRemoval
                  }
                >
                  Reset selection
                </Button>
              </>
            )}
            {/* Bulk action restrictions: disable for replica or dynamic groups */}
            {(() => {
              const selectedGroups = getSelectedGroupsData();
              const hasReplicaGroup = selectedGroups.some(
                (g) => g.syncType === "Slave"
              );
              const hasDynamicGroup = selectedGroups.some(
                (g) => g.dynamic === true
              );
              const bulkSubscribeDisabled =
                !hasSelected ||
                loadingBulkMemberSubscription ||
                loadingBulkMemberRemoval ||
                hasReplicaGroup ||
                hasDynamicGroup;
              const bulkUnsubscribeDisabled =
                !hasSelected ||
                loadingBulkMemberSubscription ||
                loadingBulkMemberRemoval ||
                hasReplicaGroup ||
                hasDynamicGroup;
              const subscribeTooltip = hasReplicaGroup
                ? "Cannot perform bulk actions on replica groups."
                : hasDynamicGroup
                ? "Cannot perform bulk actions on dynamic groups."
                : undefined;
              const unsubscribeTooltip = subscribeTooltip;
              return (
                <>
                  <span>
                    <Button
                      id="bulkMemberSubscriptionButton"
                      color="green"
                      variant="solid"
                      onClick={handleOpenBulkMemberSubscriptionModal}
                      disabled={bulkSubscribeDisabled}
                      loading={loadingBulkMemberSubscription}
                      title={
                        bulkSubscribeDisabled && subscribeTooltip
                          ? subscribeTooltip
                          : undefined
                      }
                    >
                      Bulk member subscription
                    </Button>
                  </span>
                  <span>
                    <Button
                      id="bulkMemberRemovalButton"
                      type="primary"
                      danger
                      onClick={handleOpenBulkMemberRemovalModal}
                      disabled={bulkUnsubscribeDisabled}
                      loading={loadingBulkMemberRemoval}
                      title={
                        bulkUnsubscribeDisabled && unsubscribeTooltip
                          ? unsubscribeTooltip
                          : undefined
                      }
                    >
                      Bulk member removal
                    </Button>
                  </span>
                </>
              );
            })()}
          </Space>
        </div>
      }
    >
      <Spin spinning={loading}>
        <div
          style={{
            display: "flex",
            gap: 12,
            alignItems: "center",
            marginBottom: 8,
          }}
        >
          <div style={{ flex: 1, minWidth: 280 }}>
            <SearchBar
              onSearch={handleSearch}
              searchValue={searchValue}
              onSearchValueChange={setSearchValue}
              filter={true}
              filterPlacement="before"
              allowClear={true}
              placeholder={"Search groups..."}
            />
          </div>
          <div style={{ minWidth: 240, flex: "0 0 240px" }}>
            <IdentitySearchSelect
              placeholder="Search by member"
              onSelect={handleIdentitySelect}
              onClear={clearIdentityFilter}
              value={selectedIdentity}
              style={{ width: "100%" }}
            />
          </div>
        </div>
        <ContentTable
          rowKey="id"
          rowSelection={rowSelection}
          pagination={{
            current: currentPage,
            onChange: (page) => setCurrentPage(page),
            onShowSizeChange: (current, size) => setPageSize(size),
            showSizeChanger: true,
            hideOnSinglePage: true,
            showTotal: (total) => {
              const totalText = `Total of ${total} ${
                total === 1 ? "group" : "groups"
              }`;
              if (identityFilterMode && ownGroups.length !== total) {
                return `${totalText} (filtered from ${ownGroups.length} total)`;
              }
              return totalText;
            },
            defaultPageSize: 10,
          }}
          columns={columnsWithSorting}
          dataSource={identityFilterMode ? filteredGroups : ownGroups}
        />
        {(identityFilterMode ? filteredGroups : ownGroups).length <=
          pageSize && (
          <div style={{ marginTop: 16, textAlign: "right" }}>
            <div
              style={{
                display: "inline-block",
                height: "32px",
                lineHeight: "30px",
                color: "rgba(0, 0, 0, 0.88)",
                fontSize: "14px",
              }}
            >
              Total of{" "}
              {(identityFilterMode ? filteredGroups : ownGroups).length}{" "}
              {(identityFilterMode ? filteredGroups : ownGroups).length === 1
                ? "group"
                : "groups"}
            </div>
          </div>
        )}
      </Spin>

      {/* Bulk Add/Remove Modal */}
      {(() => {
        const isOpen = bulkModalMode !== null;
        const isRemoval = bulkModalMode === "remove";
        const loadingModal = isRemoval
          ? loadingBulkMemberRemoval
          : loadingBulkMemberSubscription;
        const title = isRemoval
          ? "Bulk Remove Members from Selected Groups"
          : "Bulk Add Members to Selected Groups";
        const addButtonText = isRemoval
          ? `Remove from ${selectedRowKeys.length} Groups`
          : `Add to ${selectedRowKeys.length} Groups`;
        const onModalClose = () => setBulkModalMode(null);

        return (
          <AddMemberSearchModal
            isModalOpen={isOpen}
            onModalClose={onModalClose}
            selectedGroups={getSelectedGroupsData()}
            onSelect={
              isRemoval ? handleBulkMemberRemoval : handleBulkMemberSubscription
            }
            onCreateByEmail={handleBulkMemberSubscriptionByEmail}
            title={title}
            addButtonText={addButtonText}
            isRemovalMode={isRemoval}
            hideButton={true}
            loading={loadingModal}
            disableClose={loadingModal}
          />
        );
      })()}
      {/* Bulk Errors Modal */}
      <BulkErrorsModal
        visible={bulkErrorsVisible}
        onClose={() => setBulkErrorsVisible(false)}
        errors={bulkErrors}
      />
    </Card>
  );
};

export default GroupAdmin;
